/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

// One template per capability the engineer agent actually ships (see the
// poweralpha repo, poweralpha/services/engineer: input vetting, historical run
// lookup, loss diagnosis, parametric sweeps, weather comparison, component swap
// studies, coupled PV design scenarios, PVCosting/ESSCosting runs and cost
// breakdowns, NBA scenario advice, bnCRM project and land parcel lookups,
// sandboxed Python, knowledge base).
//
// Two rules when adding to this list: the agent must have a tool or skill for
// it, and the prompt must read like something an engineer would actually type.
export const agentPromptTemplates = {
  // Input vetting — vet_pvsim_config / vet_pvcosting_config / vet_esscosting_config
  sanityCheckConfig: (configName?: string) =>
    `Sanity-check the inputs of ${configName ?? 'one of my configs'}`,
  vetCostingConfig: () =>
    'Vet the inputs of one of my PVCosting configs against comparable historical configs',
  vetEssCostingConfig: () =>
    'Vet the inputs of one of my ESS costing configs and flag the unusual assumptions',

  // Historical runs — query_runs / compare_runs / rerun_historical_run
  summarizeRun: (service?: string, runName?: string | null) =>
    service && runName
      ? `Summarize my ${service} run of ${runName} and flag anything unusual`
      : 'Summarize my latest run and flag anything unusual',
  compareRuns: () => 'Compare two of my recent PVSim runs and show me the metric deltas',
  rerunWithTweak: (runName?: string | null) =>
    `Rerun ${runName ?? 'my last PVSim scenario'} with one input changed and tell me what moved`,
  findOutlier: () => 'Look across my recent runs and tell me which one is the outlier, and why',

  // Loss diagnosis — get_loss_waterfall / loss-diagnosis skill
  diagnoseLowPr: () => 'My performance ratio looks low — walk me through what is causing it',
  lossWaterfall: (runName?: string | null) =>
    `Show me the loss waterfall for ${runName ?? 'my latest PVSim run'} and where the biggest wins are`,

  // Sweeps and studies — bulk_run_pvsim / weather-comparison / long-term-tmy
  parametricSweep: () =>
    'Run a bounded sweep on one of my configs and show me how sensitive the results are',
  weatherComparison: () =>
    'Compare nearby TMY weather files against the one my config uses and tell me if I should switch',
  longTermTmy: () =>
    'Run a 25-year lifetime study on one of my configs and report year-over-year energy',

  // Component studies — search_modules / search_inverters / compare_components
  moduleBakeOff: () =>
    'Bake off two PV modules on one of my configs, including the spec comparison and DC/AC drift',
  inverterSwap: () => 'Swap in a different inverter on one of my configs and show me the impact',
  inverterDetails: () =>
    'Show me the derates, PF range, and MPPT limits of an inverter I am considering',

  // Coupled generation + cost design — run_pv_design_scenario
  dcAcRatioScenario: () =>
    'Test a higher DC/AC ratio on one of my PV designs and show generation against cost',
  gcrCostTradeoff: () =>
    'Sweep GCR on one of my PV designs and find the generation-versus-cost sweet spot',

  // Costing — run_pvcosting / cost breakdowns / ESS sensitivities
  capexBreakdown: () => 'Break down the capex of one of my PVCosting runs by line item',
  essCapexBreakdown: () =>
    'Break down the capex of one of my ESS costing runs, including the augmentations',
  essStorageSensitivity: () =>
    'How does the cost of one of my ESS configs change if I increase the storage energy rating?',

  // NBA (PlantSim Net Benefit Analysis) advisory
  rankNbaScenarios: () =>
    'Rank the scenarios in one of my NBA workflows — which one best fits the customer?',
  nbaRiskSpread: () =>
    'Explain the excess-energy risk and load PPA spread in one of my NBA workflows',
  explainNbaScenario: () =>
    'Explain the design variables and decision metrics behind one of my NBA scenarios',

  // bnCRM project and land — lookup_bncrm_project / sites / parcels
  lookupProject: () => 'Look up one of my projects in bnCRM and summarize its characteristics',
  landTerms: () =>
    'What are the land terms on one of my projects — lease and purchase cost, acreage, escalation?',
  landParcels: () =>
    'Summarize the parcels on one of my projects: acreage, buildability, and agreements',

  // Sandboxed Python — execute_code
  resultsTable: () => 'Build a table of the key results across my latest runs that I can download',
  plotMonthly: (runName?: string | null) =>
    `Plot monthly energy for ${runName ?? 'my latest run'} and point out the odd months`,
  customAnalysis: () => 'Run a custom Python analysis on my latest results and show me a chart',

  // Knowledge base — search_kb / read_kb
  knowledgeBaseLookup: () =>
    'Search the PowerAlpha knowledge base and explain how a modelling step actually works',
  explainConcept: () => 'Explain a PowerAlpha input or concept I should understand better',
};
